import { PageLayout } from '@/shared/layout/PageLayout';
import { useInventoryAnalytics } from './hooks/useInventoryAnalytics';
import { 
  TrendingUp, 
  Package, 
  Users, 
  AlertTriangle,
  Clock,
  Briefcase,
  DollarSign,
  Activity
} from 'lucide-react';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';

export function AnalyticsPage() {
  const { data, isLoading } = useInventoryAnalytics();

  if (isLoading || !data) {
    return (
      <PageLayout title="Báo cáo & Phân tích" description="Tổng quan dữ liệu kho và tài nguyên dự toán">
        <div className="flex items-center justify-center h-full p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Báo cáo & Phân tích" description="Tổng quan dữ liệu kho, tài nguyên và công nợ">
      <div className="flex flex-col gap-6">
        {/* Top KPIs Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-slate-500 font-medium sm:text-sm text-xs">Giá trị Tồn kho (Ước tính)</h4>
              <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">
                {data.totalInventoryValue.toLocaleString('vi-VN')} <span className="text-sm font-normal text-slate-500">VNĐ</span>
              </div>
              <div className="flex items-center gap-1 text-emerald-600 text-xs mt-2 font-medium">
                <TrendingUp className="w-3 h-3" />
                <span>+4.2% so với tháng trước</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-slate-500 font-medium sm:text-sm text-xs">Danh mục Tài nguyên</h4>
              <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                <Package className="w-5 h-5" />
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">
                {data.totalResources.toLocaleString()} <span className="text-sm font-normal text-slate-500">mã</span>
              </div>
              <div className="flex items-center gap-1 text-blue-600 text-xs mt-2 font-medium">
                <Activity className="w-3 h-3" />
                <span>{data.lowStockItems} mã sắp hết</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-slate-500 font-medium sm:text-sm text-xs">Nhà cung cấp</h4>
              <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                <Users className="w-5 h-5" />
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">
                {data.totalSuppliers.toLocaleString()} <span className="text-sm font-normal text-slate-500">đối tác</span>
              </div>
              <div className="text-slate-500 text-xs mt-2 font-medium">
                Rating TB: {data.averageRating} ⭐
              </div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-slate-500 font-medium sm:text-sm text-xs">Định mức nhân công</h4>
              <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                <Briefcase className="w-5 h-5" />
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">
                {data.totalLaborRates.toLocaleString()} <span className="text-sm font-normal text-slate-500">nhân sự</span>
              </div>
              <div className="text-slate-500 text-xs mt-2 font-medium">
                Đã thiết lập đơn giá chuẩn
              </div>
            </div>
          </div>
        </div>

        {/* Bento Grid Layout for Charts & Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Cost Breakdown Chart (CSS Custom Bar Chart) */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">Cơ cấu Chi phí Vật tư & Thiết bị</h3>
              <select className="text-sm border-slate-200 rounded-md py-1 px-2 text-slate-600 bg-slate-50">
                <option>Tháng này</option>
                <option>Tháng trước</option>
                <option>Năm nay</option>
              </select>
            </div>
            
            <div className="space-y-5 mt-4">
              {data.costBreakdown.map((item, idx) => (
                <div key={idx} className="flex flex-col gap-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-medium text-slate-700">{item.name}</span>
                    <span className="text-slate-500 font-mono">{item.value}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div 
                      className="h-2.5 rounded-full transition-all duration-1000 ease-out" 
                      style={{ width: `${item.value}%`, backgroundColor: item.color }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-rose-50/50 rounded-lg border border-rose-100">
                <AlertTriangle className="w-5 h-5 text-rose-500 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-rose-900">Cảnh báo công nợ</div>
                  <div className="text-xs text-rose-700 mt-1">Có 2 nhà cung cấp sắp đến hạn thanh toán trong 3 ngày tới.</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-emerald-50/50 rounded-lg border border-emerald-100">
                <Clock className="w-5 h-5 text-emerald-500 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-emerald-900">Giao hàng dự kiến</div>
                  <div className="text-xs text-emerald-700 mt-1">{data.pendingDeliveries} đơn nhập kho đang chờ xử lý trong tuần này.</div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Transactions List */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col">
            <div className="p-6 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">Giao dịch gần đây</h3>
            </div>
            <div className="flex-1 p-0 overflow-auto">
              <div className="divide-y divide-slate-100">
                {data.recentTransactions.map((trx) => (
                  <div key={trx.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        trx.type === 'Nhập kho' ? 'bg-emerald-100 text-emerald-600' :
                        trx.type === 'Xuất kho' ? 'bg-amber-100 text-amber-600' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        {trx.type === 'Nhập kho' ? <TrendingUp className="w-4 h-4" /> : <Activity className="w-4 h-4" />}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-800">{trx.type}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{trx.id}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold font-mono ${
                        trx.amount > 0 ? 'text-emerald-600' : 'text-slate-600'
                      }`}>
                        {trx.amount > 0 ? '+' : ''}{trx.amount.toLocaleString()} đ
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {format(new Date(trx.date), 'dd/MM/yyyy HH:mm', { locale: vi })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t border-slate-100">
              <button className="w-full py-2 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors">
                Xem tất cả lịch sử
              </button>
            </div>
          </div>

        </div>
      </div>
    </PageLayout>
  );
}

export default AnalyticsPage;
